export type OverlayOrientation = 'DESKTOP' | 'MOBILE'

export const OVERLAY_ORIENTATIONS: readonly OverlayOrientation[] = ['DESKTOP', 'MOBILE']

export interface FrameSize {
  width: number
  height: number
}

export const OVERLAY_CANVAS: Readonly<Record<OverlayOrientation, FrameSize>> = {
  DESKTOP: { width: 1920, height: 1080 },
  MOBILE: { width: 1080, height: 1920 }
}

export const ORIENTATION_LABELS: Readonly<Record<OverlayOrientation, string>> = {
  DESKTOP: 'Desktop',
  MOBILE: 'Mobile'
}

export const GRID_SIZE = 3
export const GRID_CELL_COUNT = 9
export const GRID_NO_CELLS = 0
export const GRID_ALL_CELLS = 511

export const MIN_OVERLAY_DIMENSION = 100
export const MAX_OVERLAY_DIMENSION = 4000

export const CELL_LABELS: readonly string[] = [
  'Top left',
  'Top center',
  'Top right',
  'Middle left',
  'Center',
  'Middle right',
  'Bottom left',
  'Bottom center',
  'Bottom right'
]

export interface OverlaySize {
  orientation: OverlayOrientation
  cells: number
  custom: boolean
  width: number | null
  height: number | null
}

export interface PixelBox {
  x: number
  y: number
  width: number
  height: number
}

export interface GridRect {
  row: number
  col: number
  rows: number
  cols: number
}

export interface GridArea extends GridRect {
  cells: number
  picked: number
  box: PixelBox
}

export const TOP_ROW = 7
export const MIDDLE_ROW = 56
export const BOTTOM_ROW = 448
export const LEFT_COLUMN = 73
export const CENTER_COLUMN = 146
export const RIGHT_COLUMN = 292

export const DEFAULT_OVERLAY_SIZE: Readonly<OverlaySize> = {
  orientation: 'DESKTOP',
  cells: RIGHT_COLUMN,
  custom: false,
  width: null,
  height: null
}

export const isOverlayOrientation = (v: unknown): v is OverlayOrientation =>
  v === 'DESKTOP' || v === 'MOBILE'

export const isGridCellIndex = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < GRID_CELL_COUNT

export const isGridCellMask = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= GRID_NO_CELLS && v <= GRID_ALL_CELLS

export const isOverlayDimension = (v: unknown): v is number =>
  typeof v === 'number' &&
  Number.isInteger(v) &&
  v >= MIN_OVERLAY_DIMENSION &&
  v <= MAX_OVERLAY_DIMENSION

export const clampOverlayDimension = (n: number): number =>
  Math.min(MAX_OVERLAY_DIMENSION, Math.max(MIN_OVERLAY_DIMENSION, Math.round(n)))

export const cellRow = (index: number): number => Math.floor(index / GRID_SIZE)
export const cellCol = (index: number): number => index % GRID_SIZE
export const cellBit = (index: number): number => 1 << index

export const hasCell = (mask: number, index: number): boolean =>
  isGridCellIndex(index) && (mask & cellBit(index)) !== 0

export const cellsToList = (mask: number): number[] => {
  const out: number[] = []
  for (let i = 0; i < GRID_CELL_COUNT; i++) {
    if ((mask & cellBit(i)) !== 0) out.push(i)
  }
  return out
}

export const cellsFromList = (list: readonly number[]): number => {
  let mask = GRID_NO_CELLS
  for (const i of list) {
    if (isGridCellIndex(i)) mask |= cellBit(i)
  }
  return mask
}

export const cellCount = (mask: number): number => cellsToList(mask).length

export const toggleCell = (mask: number, index: number): number =>
  isGridCellIndex(index) ? (mask ^ cellBit(index)) & GRID_ALL_CELLS : mask

const gridLine = (size: number, k: number): number => Math.round((size * k) / GRID_SIZE)

const boxOfRectIn = (frame: FrameSize, rect: GridRect): PixelBox => {
  const x = gridLine(frame.width, rect.col)
  const y = gridLine(frame.height, rect.row)
  return {
    x,
    y,
    width: gridLine(frame.width, rect.col + rect.cols) - x,
    height: gridLine(frame.height, rect.row + rect.rows) - y
  }
}

export const cellBoxIn = (frame: FrameSize, index: number): PixelBox =>
  boxOfRectIn(frame, { row: cellRow(index), col: cellCol(index), rows: 1, cols: 1 })

export const cellBox = (orientation: OverlayOrientation, index: number): PixelBox =>
  cellBoxIn(OVERLAY_CANVAS[orientation], index)

export const cellBoxes = (orientation: OverlayOrientation): PixelBox[] =>
  Array.from({ length: GRID_CELL_COUNT }, (_, i) => cellBox(orientation, i))

export const boundingRectOf = (mask: number): GridRect | null => {
  const picked = cellsToList(mask)
  if (picked.length === 0) return null
  const rows = picked.map(cellRow)
  const cols = picked.map(cellCol)
  const row = Math.min(...rows)
  const col = Math.min(...cols)
  return { row, col, rows: Math.max(...rows) - row + 1, cols: Math.max(...cols) - col + 1 }
}

export const boundingBoxIn = (frame: FrameSize, mask: number): PixelBox | null => {
  const rect = boundingRectOf(mask)
  return rect == null ? null : boxOfRectIn(frame, rect)
}

export const boundingBoxOf = (orientation: OverlayOrientation, mask: number): PixelBox | null =>
  boundingBoxIn(OVERLAY_CANVAS[orientation], mask)

export const rectMask = (rect: GridRect): number => {
  let mask = GRID_NO_CELLS
  for (let r = rect.row; r < rect.row + rect.rows; r++) {
    for (let c = rect.col; c < rect.col + rect.cols; c++) mask |= cellBit(r * GRID_SIZE + c)
  }
  return mask
}

export const isRectangular = (mask: number): boolean => {
  const rect = boundingRectOf(mask)
  return rect != null && rectMask(rect) === (mask & GRID_ALL_CELLS)
}

const lowestCell = (mask: number): number => {
  for (let i = 0; i < GRID_CELL_COUNT; i++) {
    if ((mask & cellBit(i)) !== 0) return i
  }
  return -1
}

export const connectedGroups = (mask: number): number[] => {
  let left = mask & GRID_ALL_CELLS
  const groups: number[] = []
  while (left !== GRID_NO_CELLS) {
    const start = lowestCell(left)
    let group = GRID_NO_CELLS
    const queue = [start]
    for (let i = queue.pop(); i != null; i = queue.pop()) {
      if ((left & cellBit(i)) === 0) continue
      left &= ~cellBit(i)
      group |= cellBit(i)
      const r = cellRow(i)
      const c = cellCol(i)
      if (r > 0) queue.push(i - GRID_SIZE)
      if (r < GRID_SIZE - 1) queue.push(i + GRID_SIZE)
      if (c > 0) queue.push(i - 1)
      if (c < GRID_SIZE - 1) queue.push(i + 1)
    }
    groups.push(group)
  }
  return groups
}

export const rectsOverlap = (a: GridRect, b: GridRect): boolean =>
  a.row < b.row + b.rows && b.row < a.row + a.rows && a.col < b.col + b.cols && b.col < a.col + a.cols

const unionRect = (a: GridRect, b: GridRect): GridRect => {
  const row = Math.min(a.row, b.row)
  const col = Math.min(a.col, b.col)
  return {
    row,
    col,
    rows: Math.max(a.row + a.rows, b.row + b.rows) - row,
    cols: Math.max(a.col + a.cols, b.col + b.cols) - col
  }
}

const firstOverlap = (rects: readonly GridRect[]): [number, number] | null => {
  for (let i = 0; i < rects.length; i++) {
    for (let j = i + 1; j < rects.length; j++) {
      if (rectsOverlap(rects[i], rects[j])) return [i, j]
    }
  }
  return null
}

export const chatAreaRects = (mask: number): GridRect[] => {
  let rects = connectedGroups(mask).map((g) => boundingRectOf(g)).filter((r): r is GridRect => r != null)
  for (let pair = firstOverlap(rects); pair != null; pair = firstOverlap(rects)) {
    const [i, j] = pair
    const joined = unionRect(rects[i], rects[j])
    rects = [...rects.filter((_, k) => k !== i && k !== j), joined]
  }
  return [...rects].sort((a, b) => a.row !== b.row ? a.row - b.row : a.col - b.col)
}

export const chatAreasIn = (frame: FrameSize, mask: number): GridArea[] =>
  chatAreaRects(mask).map((rect) => {
    const cells = rectMask(rect)
    return { ...rect, cells, picked: mask & cells, box: boxOfRectIn(frame, rect) }
  })

export const chatAreas = (orientation: OverlayOrientation, mask: number): GridArea[] =>
  chatAreasIn(OVERLAY_CANVAS[orientation], mask)

export const fillerCellsOf = (mask: number): number =>
  chatAreaRects(mask).reduce((acc, r) => acc | rectMask(r), GRID_NO_CELLS) & ~mask & GRID_ALL_CELLS

export const gridKeyTarget = (index: number, key: string): number | null => {
  const row = cellRow(index)
  const col = cellCol(index)
  switch (key) {
    case 'ArrowRight': return col < GRID_SIZE - 1 ? index + 1 : index
    case 'ArrowLeft': return col > 0 ? index - 1 : index
    case 'ArrowDown': return row < GRID_SIZE - 1 ? index + GRID_SIZE : index
    case 'ArrowUp': return row > 0 ? index - GRID_SIZE : index
    case 'Home': return 0
    case 'End': return GRID_CELL_COUNT - 1
    default: return null
  }
}

export const withCellToggled = (value: OverlaySize, index: number): OverlaySize =>
  value.custom || !isGridCellIndex(index)
    ? value
    : { ...value, cells: toggleCell(value.cells, index) }

export const withOrientation = (value: OverlaySize, orientation: OverlayOrientation): OverlaySize =>
  value.custom || value.orientation === orientation ? value : { ...value, orientation }

export const withCustom = (value: OverlaySize, on: boolean): OverlaySize => {
  if (value.custom === on) return value
  if (!on) return { ...value, custom: false }
  if (value.width != null && value.height != null) return { ...value, custom: true }
  const seed = boundingBoxOf(value.orientation, value.cells) ?? {
    ...OVERLAY_CANVAS[value.orientation],
    x: 0,
    y: 0
  }
  return {
    ...value,
    custom: true,
    width: clampOverlayDimension(seed.width),
    height: clampOverlayDimension(seed.height)
  }
}

export const withCustomDimension = (
  value: OverlaySize,
  side: 'width' | 'height',
  n: number
): OverlaySize => {
  if (!Number.isFinite(n)) return value
  const next = clampOverlayDimension(n)
  return value[side] === next ? value : { ...value, [side]: next }
}

export const overlayFrameOf = (value: OverlaySize): FrameSize =>
  value.custom && value.width != null && value.height != null
    ? { width: value.width, height: value.height }
    : { ...OVERLAY_CANVAS[value.orientation] }

const NAMED_SHAPES: ReadonlyMap<number, string> = new Map([
  [GRID_ALL_CELLS, 'Full screen'],
  [TOP_ROW, 'Top row'],
  [MIDDLE_ROW, 'Middle row'],
  [BOTTOM_ROW, 'Bottom row'],
  [LEFT_COLUMN, 'Left column'],
  [CENTER_COLUMN, 'Center column'],
  [RIGHT_COLUMN, 'Right column'],
  [TOP_ROW | MIDDLE_ROW, 'Top two rows'],
  [MIDDLE_ROW | BOTTOM_ROW, 'Bottom two rows'],
  [LEFT_COLUMN | CENTER_COLUMN, 'Left two columns'],
  [CENTER_COLUMN | RIGHT_COLUMN, 'Right two columns']
])

export const PICK_A_CELL_COPY = 'Pick at least one cell, or turn on Custom pixel dimensions.'

export const shapeNameOf = (mask: number): string | null => {
  const m = mask & GRID_ALL_CELLS
  const named = NAMED_SHAPES.get(m)
  if (named != null) return named
  const picked = cellsToList(m)
  if (picked.length === 0) return null
  if (picked.length === 1) return `${CELL_LABELS[picked[0]]} cell`
  return `${picked.length} cells`
}

export const describeOverlaySize = (value: OverlaySize): string => {
  if (value.custom && value.width != null && value.height != null) {
    return `Custom size, ${value.width} x ${value.height}.`
  }
  const name = shapeNameOf(value.cells)
  if (name == null) return `No cells picked. ${PICK_A_CELL_COPY}`
  const areas = chatAreas(value.orientation, value.cells)
  if (areas.length === 1) {
    const [{ box, cells, picked }] = areas
    if (cells === picked) return `${name}, ${box.width} x ${box.height}.`
    return `${name}. The chat fills the box around them, ${box.width} x ${box.height}.`
  }
  return `${name} in ${areas.length} areas. Each area gets its own copy of the chat.`
}

export const overlaySizeProblem = (value: OverlaySize): string | null => {
  if (!isOverlayOrientation(value.orientation)) return 'Orientation must be Desktop or Mobile.'
  if (!isGridCellMask(value.cells)) return 'Cells must be a whole number from 0 to 511.'
  if (typeof value.custom !== 'boolean') return 'Custom pixel dimensions must be on or off.'
  if ((value.width == null) !== (value.height == null)) {
    return 'Width and height must both be set or both be empty.'
  }
  if (value.custom && (value.width == null || value.height == null)) {
    return 'Custom pixel dimensions need a width and a height.'
  }
  if (value.width != null && (!isOverlayDimension(value.width) || !isOverlayDimension(value.height))) {
    return `Width and height must each be a whole number from ${MIN_OVERLAY_DIMENSION} to ${MAX_OVERLAY_DIMENSION} pixels.`
  }
  if (!value.custom && value.cells === GRID_NO_CELLS) return PICK_A_CELL_COPY
  return null
}

export interface StoredOverlaySize {
  orientation?: string | null
  gridCells?: number | null
  customSize?: boolean | null
  width?: number | null
  height?: number | null
}

export const storedPairOf = (row: StoredOverlaySize): FrameSize | null => {
  const w = row.width
  const h = row.height
  if (typeof w !== 'number' || !Number.isFinite(w)) return null
  if (typeof h !== 'number' || !Number.isFinite(h)) return null
  return { width: clampOverlayDimension(w), height: clampOverlayDimension(h) }
}

export const storedGridOf = (row: StoredOverlaySize): { orientation: OverlayOrientation, cells: number } | null => {
  const { orientation, gridCells } = row
  if (!isOverlayOrientation(orientation)) return null
  if (!isGridCellMask(gridCells) || gridCells === GRID_NO_CELLS) return null
  return { orientation, cells: gridCells }
}

const LEGACY_FULL_SCREEN = OVERLAY_CANVAS.DESKTOP

export const overlaySizeFromRow = (row: StoredOverlaySize): OverlaySize => {
  const pair = storedPairOf(row)
  const grid = storedGridOf(row)
  const remembered = pair ?? { width: null, height: null }
  if (grid != null) {
    return { orientation: grid.orientation, cells: grid.cells, custom: row.customSize === true && pair != null, ...remembered }
  }
  if (row.customSize === true && pair != null) return { ...DEFAULT_OVERLAY_SIZE, custom: true, ...pair }
  if (pair != null) {
    if (pair.width === LEGACY_FULL_SCREEN.width && pair.height === LEGACY_FULL_SCREEN.height) {
      return { orientation: 'DESKTOP', cells: GRID_ALL_CELLS, custom: false, ...pair }
    }
    return { ...DEFAULT_OVERLAY_SIZE, custom: true, ...pair }
  }
  return { ...DEFAULT_OVERLAY_SIZE }
}

export interface OverlaySizeWire {
  customSize: boolean
  orientation?: OverlayOrientation
  gridCells?: number
  width?: number
  height?: number
}

export const overlaySizeWire = (value: OverlaySize): OverlaySizeWire => {
  const cells = value.cells & GRID_ALL_CELLS
  return {
    customSize: value.custom,
    ...(cells !== GRID_NO_CELLS && { orientation: value.orientation, gridCells: cells }),
    ...(value.width != null && value.height != null && { width: value.width, height: value.height })
  }
}

export type OverlayRenderMode =
  | { kind: 'box', width: number, height: number }
  | { kind: 'grid', orientation: OverlayOrientation, cells: number }
  | { kind: 'fill' }

export const overlayRenderModeOf = (row: StoredOverlaySize): OverlayRenderMode => {
  const pair = storedPairOf(row)
  const grid = storedGridOf(row)
  if (row.customSize === true && pair != null) return { kind: 'box', ...pair }
  if (grid != null) return { kind: 'grid', ...grid }
  if (pair != null) return { kind: 'box', ...pair }
  return { kind: 'fill' }
}

export const gridObsRecipeOf = (orientation: OverlayOrientation): string => {
  const { width, height } = OVERLAY_CANVAS[orientation]
  return `Source size: ${width * 2} x ${height * 2}, then right-click the source and choose Transform, Fit to screen (Scale Filtering: Area).`
}
